import {
  AI_ATTACHMENT_SPREADSHEET_MIME_TYPE,
  UPLOAD_CAPABILITY_CONTRACT,
  type UploadCapability,
  type UploadResourceType,
  type UploadSelectionFormat,
} from '@repo/common';

import { prepareImageForUpload } from '../utils/image/convert-image-to-webp';
import { fetchWithCsrf } from './fetch-client';

interface Options {
  resourceType?: UploadResourceType;
}

interface UploadConfig {
  apiUri: string;
}

export interface FileResourceReference {
  relativePath: string;
  fileName?: string | null;
  resourceType?: string;
  size?: number | null;
}

export interface UploadedFileResource extends FileResourceReference {
  id: string;
  fileName: string;
  resourceType: string;
}

const UPLOADED_FILE_CLEANUP_TIMEOUT_MS = 15_000;
const GENERIC_ZIP_MIME_TYPES = new Set(['application/zip', 'application/x-zip-compressed']);

export class PictureSavePersistenceError extends Error {
  readonly canCleanupUploads: boolean;

  constructor(message: string, canCleanupUploads: boolean, cause?: unknown) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = 'PictureSavePersistenceError';
    this.canCleanupUploads = canCleanupUploads;
  }
}

// Default config that can be overridden
let config: UploadConfig = {
  apiUri: '/api', // Default fallback
};

export function configureFileUpload(newConfig: Partial<UploadConfig>) {
  config = { ...config, ...newConfig };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseUploadedFileResource(value: unknown): UploadedFileResource {
  if (
    !isRecord(value) ||
    typeof value.id !== 'string' ||
    typeof value.relativePath !== 'string' ||
    typeof value.fileName !== 'string' ||
    typeof value.resourceType !== 'string'
  ) {
    throw new Error('File upload returned an invalid resource');
  }

  return {
    id: value.id,
    relativePath: value.relativePath,
    fileName: value.fileName,
    resourceType: value.resourceType,
    ...(typeof value.size === 'number' ? { size: value.size } : {}),
  };
}

function getFileExtension(fileName: string): string | undefined {
  const extension = fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() : undefined;
  return extension && extension.length > 0 ? extension : undefined;
}

function selectionFormatMatchesFile(format: UploadSelectionFormat, file: File): boolean {
  const mimeType = file.type.toLowerCase();
  const extension = getFileExtension(file.name);
  return format.mimeTypes.includes(mimeType) || (extension !== undefined && format.extensions.includes(extension));
}

function getUploadCapability(resourceType: UploadResourceType): UploadCapability {
  const capability = UPLOAD_CAPABILITY_CONTRACT.find(candidate => candidate.resourceType === resourceType);
  if (!capability) {
    throw new Error(`Unsupported upload resource type: ${resourceType}`);
  }
  return capability;
}

function fileMatchesWireFormat(file: File, capability: UploadCapability): boolean {
  const mimeType = file.type.toLowerCase();
  const extension = getFileExtension(file.name);
  return capability.wireFormats.some(
    format => format.mimeType === mimeType && extension !== undefined && format.extensions.includes(extension),
  );
}

function normalizeExtensionMatchedFile(file: File, capability: UploadCapability): File {
  const extension = getFileExtension(file.name);
  if (extension === undefined) return file;
  const wireFormat = capability.wireFormats.find(format => format.extensions.includes(extension));
  if (wireFormat === undefined || file.type.toLowerCase() === wireFormat.mimeType) return file;
  const browserMimeType = file.type.toLowerCase();
  const isGenericXlsxArchive =
    wireFormat.mimeType === AI_ATTACHMENT_SPREADSHEET_MIME_TYPE && GENERIC_ZIP_MIME_TYPES.has(browserMimeType);
  if (
    !wireFormat.mimeType.startsWith('text/') &&
    browserMimeType !== '' &&
    browserMimeType !== 'application/octet-stream' &&
    !isGenericXlsxArchive
  ) {
    return file;
  }
  return new File([file], file.name, { type: wireFormat.mimeType, lastModified: file.lastModified });
}

export function inferUploadResourceType(file: File): UploadResourceType {
  const capability = UPLOAD_CAPABILITY_CONTRACT.find(candidate =>
    candidate.selectionFormats.some(format => selectionFormatMatchesFile(format, file)),
  );

  if (!capability) {
    throw new Error(`Unsupported upload format: ${file.name}`);
  }
  return capability.resourceType;
}

export async function uploadFile(file: File, options: Options = {}): Promise<UploadedFileResource> {
  const resourceType = options.resourceType ?? inferUploadResourceType(file);
  const capability = getUploadCapability(resourceType);
  const isSupportedSelection = capability.selectionFormats.some(format => selectionFormatMatchesFile(format, file));
  if (!isSupportedSelection) {
    throw new Error(`File format is not supported for ${resourceType}`);
  }
  if (file.size > capability.maxBytes) {
    throw new Error(`File exceeds the size limit for ${resourceType}`);
  }

  const wireFile =
    resourceType === 'IMAGE' ? await prepareImageForUpload(file) : normalizeExtensionMatchedFile(file, capability);
  if (wireFile.size > capability.maxBytes) {
    throw new Error(`File exceeds the size limit for ${resourceType}`);
  }
  if (!fileMatchesWireFormat(wireFile, capability)) {
    throw new Error(`File must be converted to a supported wire format for ${resourceType}`);
  }

  const formData = new FormData();
  formData.append('file', wireFile);
  formData.append('resourceType', resourceType);

  const response = await fetchWithCsrf(`${config.apiUri}/storage/upload`, {
    method: 'POST',
    body: formData,
    redirect: 'follow',
  });

  if (!response.ok) {
    throw new Error(`File upload failed: ${response.status} ${response.statusText}`);
  }

  const payload: unknown = await response.json();
  return parseUploadedFileResource(payload);
}

export async function deleteUploadedFile(resource: UploadedFileResource): Promise<void> {
  const response = await fetchWithCsrf(`${config.apiUri}/storage/resources/${encodeURIComponent(resource.id)}`, {
    method: 'DELETE',
    signal: AbortSignal.timeout(UPLOADED_FILE_CLEANUP_TIMEOUT_MS),
  });

  if (!response.ok) {
    throw new Error(`Uploaded file cleanup failed: ${response.status} ${response.statusText}`);
  }
}
